/**
 * Temporary data that is missing from the API. Remove each export once the API is updated.
 */

/**
 * LOB products missing from the API.
 * @example { lineOfBusinessCode: "LB_01", code: "PD_0055", display: "PD_0055 - Banking", description: "Banking" }
 */
export const tempMissingLobProducts = [
  { lineOfBusinessCode: "LB_01", code: "PD_0055", display: "PD_0055 - Banking", description: "Banking" },
  { lineOfBusinessCode: "LB_01", code: "PD_0057", display: "PD_0057 - AI BAS", description: "AI BAS" },
];

/**
 * Display name overrides for LOB products whose descriptions changed but the API has not yet been updated.
 */
export const tempProductDisplayOverrides = {
  PD_0018: "PD_0018 - Exo ES",
  PD_0021: "PD_0021 - MYOB Acumatica People",
};

/**
 * Display name overrides for cost centres whose descriptions changed but the API has not yet been updated.
 * @example { CC_076: "CC_076 - Flare - Technology S&M" }
 */
export const tempCostCentreDisplayOverrides = {
  CC_076: "CC_076 - Flare - Technology S&M",
};

/**
 * Cost centres not yet available from the API.
 * Each entry must match the shape returned by GET /api/org/cost-centres.
 * @example { code: "CC_078", display: "CC_078 - Reliability & Security - G&A", description: "Reliability & Security - G&A", function: "Technology" }
 */
export const tempMissingCostCentres = [
  {
    code: "CC_078",
    display: "CC_078 - Reliability & Security - G&A",
    description: "Reliability & Security - G&A",
    function: "Technology",
  },
  { code: "CC_079", display: "CC_079 - Technology - S&M", description: "Technology - S&M", function: "Technology" },
];

/**
 * Project codes not yet available from the API.
 * Each entry must match the shape returned by GET /api/org/projects.
 * @example { code: "PJ_0046", display: "PJ_0046 - AI Transformation", description: "AI Transformation" }
 */
export const tempMissingProjects = [
  {
    code: "PJ_0046",
    display: "PJ_0046 - AI Transformation",
    description: "AI Transformation",
  },
];

/**
 * Function/group/department/team combinations missing from the API.
 * @example { function: "Technology", group: "Emerging Platforms", department: "MAP", team: "N/A" }
 */
export const tempMissingFunctionData = [
  { function: "SME & Practice", group: "Solo & Embedded Finance", department: "Solo", team: "General" },
  { function: "Technology", group: "Emerging Platforms", department: "CRM", team: "Skylorians & Leadership" },
  { function: "Technology", group: "Data and AI", department: "AI Products", team: "AI Chat" },
  { function: "Technology", group: "Data and AI", department: "AI Products", team: "AI BAS" },
  { function: "Technology", group: "Data and AI", department: "AI Products", team: "AI Expenses" },
  { function: "Technology", group: "Emerging Platforms", department: "Solo and Apps", team: "Solo Web" },
  {
    function: "SME & Practice",
    group: "Practice Sales Retention &Serv",
    department: "Practice Marketing",
    team: "Accountant and Bookkeeper Marketing",
  },
  {
    function: "SME & Practice",
    group: "Retention",
    department: "Customer Strategy & Performance",
    team: "Retention Projects",
  },
  {
    function: "Group Services",
    group: "Commercial Finance Enterprise",
    department: "Mid Market Operations",
    team: "Operations",
  },
  {
    function: "Group Services",
    group: "Commercial Finance Enterprise",
    department: "Mid Market Operations",
    team: "Operations & Billing",
  },
  { function: "Flare", group: "Cars & Salary Packaging", department: "Marketing", team: "N/A" },
  { function: "Flare", group: "Cars & Salary Packaging", department: "Customer Success", team: "N/A" },
  { function: "Flare", group: "Cars & Salary Packaging", department: "Sales", team: "Health Insurance" },
  { function: "Flare", group: "Network", department: "Not Applicable", team: "Not Applicable" },
  { function: "Flare", group: "Product & Technology", department: "Data & Analytics", team: "Not Applicable" },
  { function: "SME & Practice", group: "Prod, Prod Mktg & Design", department: "Design", team: "SME Design" },
  { function: "SME & Practice", group: "Prod, Prod Mktg & Design", department: "Design", team: "Practice Design" },
  { function: "SME & Practice", group: "Prod, Prod Mktg & Design", department: "Design", team: "Not Applicable" },
  { function: "SME & Practice", group: "Prod, Prod Mktg & Design", department: "Product", team: "SME Product" },
  {
    function: "SME & Practice",
    group: "Prod, Prod Mktg & Design",
    department: "Product Marketing",
    team: "SME Product Marketing",
  },
  { function: "SME & Practice", group: "Practice Prod & Prod Mktg", department: "Product", team: "Practice Product" },
  {
    function: "SME & Practice",
    group: "Practice Prod & Prod Mktg",
    department: "Product Marketing",
    team: "Practice Product Marketing",
  },
  {
    function: "Mid-Market",
    group: "Customer Experience&Operations",
    department: "Enablement",
    team: "Hubspot COE",
  },
  // New "Data Insights & Analytics" group with its departments and teams (not yet in the API).
  {
    function: "Group Services",
    group: "Data Insights & Analytics",
    department: "Operations Analytics",
    team: "Mid-Market",
  },
  {
    function: "Group Services",
    group: "Data Insights & Analytics",
    department: "Operations Analytics",
    team: "SMEP",
  },
  {
    function: "Group Services",
    group: "Data Insights & Analytics",
    department: "Operations Analytics",
    team: "Embedded Finance",
  },
  {
    function: "Group Services",
    group: "Data Insights & Analytics",
    department: "Product Analytics",
    team: "Not Applicable",
  },
  {
    function: "Group Services",
    group: "Data Insights & Analytics",
    department: "Data Science",
    team: "Not Applicable",
  },
  {
    function: "Product",
    group: "Not Applicable",
    department: "Not Applicable",
    team: "Not Applicable",
  },
];

/**
 * Locations not yet available from the API.
 * Each entry must match the shape returned by GET /api/org/region-locations.
 */
export const ADDITIONAL_LOCATIONS = [
  {
    locationCode: "LO_022",
    locationDisplay: "LO_022 - Remote - Sri Lanka",
    locationDescription: "Remote - Sri Lanka",
    regionCode: "RE_013",
    regionDisplay: "RE_013 - Other International",
    regionDescription: "Other International",
    countryCode: "LK",
    countryDisplay: "LK - Sri Lanka",
    countryDescription: "Sri Lanka",
  },
];
